import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { StrategyListItemResponse } from '../../../server/response/responseScheme';
import firebaseStore from '../../stores/firebaseStore';
import Box from '../../uikit/Box';
import { apiClient } from '../../utils/apiClient';
import { useMobxInstance } from '../../utils/mobxUtil';
import { useCommonNavigate } from '../common/CommonLink';
import CreateStrategyCard from './CreateStrategyCard';
import { ChevronDownIcon, PlusIcon } from 'lucide-react';
import _ from 'lodash';
import dayjs from 'dayjs';
import ChangeValue from '../common/ChangeValue';
import modalStore from '../../stores/modalStore';

type PeriodType = 'daily' | 'monthly' | 'yearly' | 'total';

class ViewStore {
    strategies: StrategyListItemResponse[] = [];
    isLoading = true; // 기본은 로딩 상태
    isLoggedIn = false;
    selectedPeriod: PeriodType = 'daily'; // 기본값: 어제

    constructor() {
        makeAutoObservable(this);
    }

    setPeriod(period: PeriodType) {
        this.selectedPeriod = period;
    }

    // 선택된 기간에 따른 수익률 반환
    getReturnByPeriod(latestNav: StrategyListItemResponse['latestNav']): number {
        if (!latestNav) {
            return 0;
        }

        switch (this.selectedPeriod) {
            case 'daily':
                return latestNav.dailyReturn;
            case 'monthly':
                return latestNav.monthlyReturn ?? 0;
            case 'yearly':
                return latestNav.yearlyReturn ?? 0;
            case 'total':
                return latestNav.totalReturn ?? 0;
            default:
                return 0;
        }
    }

    // 기간 라벨 반환 (현재 월/년 표시)
    get periodLabel(): string {
        const currentMonth = dayjs().format('M월');
        const currentYear = dayjs().format('YYYY년');

        switch (this.selectedPeriod) {
            case 'daily':
                return '어제';
            case 'monthly':
                return currentMonth;
            case 'yearly':
                return currentYear;
            case 'total':
                return '누적';
            default:
                return '어제';
        }
    }

    async fetch() {
        this.isLoading = true;

        const loggedIn = await firebaseStore.isLoggedIn();

        runInAction(() => {
            this.isLoggedIn = loggedIn;
        });

        if (!loggedIn) {
            runInAction(() => {
                this.isLoading = false;
            });
            return;
        }

        try {
            const strategies = await apiClient.strategy.member.getMyList.query({
                isActive: true,
            });

            runInAction(() => {
                this.strategies = strategies;
                this.isLoading = false;
            });
        } catch (error) {
            console.error('전략 목록 조회 실패:', error);
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }
}

const MyStrategyList = observer(() => {
    const viewStore = useMobxInstance(ViewStore);
    const { navigate } = useCommonNavigate();

    useEffect(() => {
        viewStore.fetch();
    }, [viewStore]);

    // 로딩 중
    if (viewStore.isLoading) {
        return <Box isLoading={true} />;
    }

    // 로그인하지 않았거나 또는 전략이 없는 경우 - 전략 생성 카드만 표시
    if (!viewStore.isLoggedIn || viewStore.strategies.length === 0) {
        return (
            <Box>
                <CreateStrategyCard />
            </Box>
        );
    }

    // 전략이 있는 경우 - 상세 전략 목록 표시
    return (
        <Box>
            {/* 헤더: 타이틀 + 기간 선택 */}
            <Box.Title title="내 전략" />
            <button
                className="btn btn-ghost btn-sm text-base-content/60 absolute top-2 right-2 gap-1"
                onClick={async (e) => {
                    e.preventDefault();

                    const { returnValue: selectedPeriod } = await modalStore.select<PeriodType>({
                        title: '수익률 기간',
                        items: [
                            { name: '어제', value: 'daily' as PeriodType },
                            // TODO 오늘이 아니라 기준 날짜로 해야 함
                            { name: dayjs().format('M월'), value: 'monthly' as PeriodType },
                            { name: dayjs().format('YYYY년'), value: 'yearly' as PeriodType },
                            { name: '누적', value: 'total' as PeriodType },
                        ],
                        showRadio: false,
                        selectedValue: viewStore.selectedPeriod,
                    });

                    if (selectedPeriod) {
                        viewStore.setPeriod(selectedPeriod);
                    }
                }}
            >
                {viewStore.periodLabel} 수익률
                <ChevronDownIcon className="size-4" />
            </button>
            <div className="flex flex-col gap-3">
                {viewStore.strategies.map((strategy) => {
                    const latestNav = strategy.latestNav;
                    const latestWeight = strategy.latestWeight;
                    const weights = latestWeight?.weightsJson || {};
                    const weightEntries = _.orderBy(
                        Object.entries(weights),
                        ([, weight]) => weight,
                        'desc',
                    );
                    const strategyReturn = viewStore.getReturnByPeriod(latestNav);

                    return (
                        <button
                            key={strategy.hash}
                            className="card bg-base-200 hover:bg-base-300 cursor-pointer p-4 text-left transition-colors"
                            onClick={() => navigate(`/strategy/${strategy.hash}`)}
                        >
                            <div className="flex flex-col gap-3">
                                {/* 헤더: 전략명과 수익률 */}
                                <div className="flex items-baseline justify-between gap-2">
                                    <div className="truncate font-bold">{strategy.name}</div>
                                    <div className="shrink-0 text-lg font-bold tabular-nums">
                                        <ChangeValue
                                            value={strategyReturn}
                                            sign={true}
                                            round={1}
                                            suffix="%"
                                        />
                                    </div>
                                </div>

                                {/* 구성 종목과 수익률 */}
                                {weightEntries.length > 0 && (
                                    <div className="flex flex-col gap-1">
                                        {weightEntries.map(([ticker, weight]) => (
                                            <div
                                                key={ticker}
                                                className="flex items-center justify-between text-sm"
                                            >
                                                <div className="text-base-content/80">
                                                    {ticker}{' '}
                                                    <span className="text-base-content/50">
                                                        {(weight * 100).toFixed(0)}%
                                                    </span>
                                                </div>
                                                <div className="tabular-nums">
                                                    {/* TODO: 종목별 수익률 계산 로직 구현 필요 (StockPriceService 활용) */}
                                                    <ChangeValue
                                                        value={0}
                                                        sign={true}
                                                        round={1}
                                                        suffix="%"
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </button>
                    );
                })}
                <button
                    className="btn btn-ghost btn-block text-base-content/50 mt-2"
                    onClick={(e) => {
                        e.preventDefault();
                        navigate('/strategy/create');
                    }}
                >
                    <PlusIcon className="size-4" />
                    추가하기
                </button>
            </div>
        </Box>
    );
});

export default MyStrategyList;
